import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, TextField, InputAdornment,
  Avatar, Tooltip, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, IconButton, Button, TablePagination,
} from '@mui/material';
import {
  Assignment, People, BookOnline, Search, CheckCircle,
  Cancel, HourglassEmpty, Category,
  TrendingUp, TrendingDown, Close as CloseIcon,
} from '@mui/icons-material';
import managerService from '../../services/managerService';
import serviceRequestService from '../../services/serviceRequestService';
import moment from 'moment';

// ─── helpers ──────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  const [visited, setVisited] = useState(false);
  useEffect(() => { if (value === index) setVisited(true); }, [value, index]);
  if (!visited) return null;
  return <Box sx={{ pt: 3, display: value === index ? 'block' : 'none' }}>{children}</Box>;
}

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h4" fontWeight="bold">{value}</Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: `${color}.light`, borderRadius: 2, p: 1.5 }}>
            {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── status chips ─────────────────────────────────────────────────────────────

function BookingStatusChip({ status }) {
  const map = {
    pending:     { label: 'Pending',     color: 'warning' },
    'in-progress': { label: 'In Progress', color: 'info'    },
    completed:   { label: 'Completed',   color: 'success' },
    cancelled:   { label: 'Cancelled',   color: 'error'   },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function AvatarInitials({ name, color = 'primary' }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: `${color}.main` }}>
      {initials}
    </Avatar>
  );
}

// ─── TAB 1 : REQUESTS ─────────────────────────────────────────────────────────

// Sub-tab A: Active Requests (unique-by-user) with Partners modal
function ActiveRequestsSection() {
  const [requests, setRequests]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [search, setSearch]               = useState('');
  const [searchInput, setSearchInput]     = useState('');

  // Partners modal
  const [showPartners, setShowPartners]   = useState(false);
  const [partners, setPartners]           = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [activeReq, setActiveReq]         = useState(null);

  useEffect(() => { load(search); }, [search]);

  const load = async (q = '') => {
    try {
      setLoading(true);
      setError('');
      const data = await managerService.getRequests(q);
      setRequests(data || []);
    } catch (e) {
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const openPartners = async (req) => {
    setActiveReq(req);
    setShowPartners(true);
    setPartnersLoading(true);
    try {
      const data = await managerService.getRequestPartners(req.subCategoryId, req.userId);
      setPartners(data || []);
    } catch (e) {
      setPartners([]);
    } finally {
      setPartnersLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by name / mobile / pincode…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <Button variant="contained" onClick={handleSearch} sx={{ px: 3 }}>Search</Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
        Total Requests: <strong>{requests.length}</strong>
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Customer Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>City</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Sub Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Requirements</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Requested At</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Partners</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No requests found</Typography>
                </TableCell>
              </TableRow>
            ) : requests.map((r, idx) => (
              <TableRow
                key={r.requestId || idx}
                sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}
                hover
              >
                <TableCell>{r.user?.name || '—'}</TableCell>
                <TableCell>{r.user?.mobileNumber || '—'}</TableCell>
                <TableCell>{r.user?.city || r.city || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                    {r.fullAddress || r.user?.fullAddress || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{r.category || '—'}</TableCell>
                <TableCell>{r.subCategory || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                    {r.requirement || '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {r.createdAt ? moment(r.createdAt).fromNow() : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => openPartners(r)}
                  >
                    Partners
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Partners Modal */}
      <Dialog open={showPartners} onClose={() => setShowPartners(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Requested Partners</Typography>
            {activeReq && (
              <Typography variant="caption" color="text.secondary">
                {activeReq.category} → {activeReq.subCategory} &nbsp;|&nbsp; Customer: {activeReq.user?.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setShowPartners(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {partnersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : partners.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No partner records found for this request
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Partner ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Pincode</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partners.map((p, i) => (
                    <TableRow key={p.requestId || i} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {p.partner?.uniqueId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AvatarInitials name={p.partner?.name} color={p.status === 'accepted' ? 'success' : 'warning'} />
                          <Typography variant="body2">{p.partner?.name || '—'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{p.partner?.mobileNumber || '—'}</TableCell>
                      <TableCell>{p.partner?.city || '—'}</TableCell>
                      <TableCell>{p.partner?.pincode || '—'}</TableCell>
                      <TableCell>
                        {p.status === 'accepted' && <Chip label="Accepted" color="success" size="small" />}
                        {p.status === 'requested' && <Chip label="Sent / Pending" color="warning" size="small" />}
                        {p.status !== 'accepted' && p.status !== 'requested' &&
                          <Chip label={p.status || 'Unknown'} size="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// Sub-tab B: Request Logs (cancelled / not-accepted) — mirrors admin CancledRequest.js
function RequestLogsSection() {
  const [logs, setLogs]           = useState([]);
  const [totalCount, setTotal]    = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRPP]     = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [data, count] = await Promise.all([
        managerService.getRequestLogs(page, rowsPerPage, search),
        managerService.getRequestLogsCount(search),
      ]);
      setLogs(data || []);
      setTotal(count);
    } catch (e) {
      setError('Failed to load request logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by service name…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <Button variant="contained" onClick={handleSearch} sx={{ px: 3 }}>Search</Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
        Total Logs: <strong>{totalCount}</strong>
      </Typography>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Service Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Accepted Partners</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Not Accepted Partners</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Requested At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No request logs found</Typography>
                  </TableCell>
                </TableRow>
              ) : logs.map((log, idx) => (
                <TableRow
                  key={log.logId || idx}
                  sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}
                  hover
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{log.serviceName || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AvatarInitials name={log.user?.name} color="primary" />
                      <Box>
                        <Typography variant="body2">{log.user?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{log.user?.mobileNumber || ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={(log.acceptedPartners?.length || 0)}
                        color={(log.acceptedPartners?.length || 0) > 0 ? 'success' : 'default'}
                        size="small"
                      />
                      {(log.acceptedPartners?.length || 0) > 0 && (
                        <Tooltip title={log.acceptedPartners.join(', ')}>
                          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                            view names
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={(log.notAcceptedPartners?.length || 0)}
                        color={(log.notAcceptedPartners?.length || 0) > 0 ? 'error' : 'default'}
                        size="small"
                      />
                      {(log.notAcceptedPartners?.length || 0) > 0 && (
                        <Tooltip title={log.notAcceptedPartners.join(', ')}>
                          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                            view names
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.customerReason || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {log.requestedAt ? moment(log.requestedAt).fromNow() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRPP(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}

function RequestsTab() {
  const [subTab, setSubTab] = useState(0);
  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
          <Tab label="Active Requests" icon={<Assignment />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Request Logs (Not Accepted)" icon={<Cancel />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
      </Paper>
      {subTab === 0 && <ActiveRequestsSection />}
      {subTab === 1 && <RequestLogsSection />}
    </Box>
  );
}

// ─── TAB 2 : PARTNER CATEGORY STATS ──────────────────────────────────────────

// Threshold per category — manager can conceptually know how many they "need"
const CATEGORY_TARGET = 15; // default target per category

function PartnerCategoriesTab() {
  const [partners, setPartners]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [partnersData, catsData] = await Promise.all([
        managerService.getPartnersWithServices(),
        managerService.getCategories(),
      ]);
      setPartners(partnersData || []);
      setCategories(catsData || []);
    } catch (e) {
      setError('Failed to load partner / category data');
    } finally {
      setLoading(false);
    }
  };

  // Build per-category stats
  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.categoryId] = {
      name: cat.name,
      type: cat.categoryType,
      icon: cat.iconUrl,
      partners: [],
      verified: 0,
      unverified: 0,
    };
  });

  // Each partner can have multiple services across multiple categories
  partners.forEach(partner => {
    const seenCats = new Set();
    (partner.services || []).forEach(svc => {
      if (svc.categoryId && !seenCats.has(svc.categoryId)) {
        seenCats.add(svc.categoryId);
        if (!categoryMap[svc.categoryId]) {
          categoryMap[svc.categoryId] = {
            name: svc.categoryName || 'Unknown',
            type: '—',
            partners: [],
            verified: 0,
            unverified: 0,
          };
        }
        categoryMap[svc.categoryId].partners.push(partner);
        if (partner.status === 'verified') categoryMap[svc.categoryId].verified++;
        else categoryMap[svc.categoryId].unverified++;
      }
    });
  });

  const rows = Object.entries(categoryMap).map(([catId, data]) => ({
    categoryId: catId,
    ...data,
    total: data.partners.length,
    needed: Math.max(0, CATEGORY_TARGET - data.partners.length),
  })).sort((a, b) => b.total - a.total);

  const totalPartners  = partners.length;
  const verifiedTotal  = partners.filter(p => p.status === 'verified').length;
  const catsWithPartners = rows.filter(r => r.total > 0).length;
  const catsNeedMore   = rows.filter(r => r.needed > 0).length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total Partners" value={totalPartners} icon={<People />} color="primary" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Verified Partners" value={verifiedTotal} icon={<CheckCircle />} color="success" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Categories Active" value={catsWithPartners} icon={<Category />} color="info" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Categories Need More" value={catsNeedMore} icon={<TrendingDown />} color="warning"
            subtitle={`Target: ${CATEGORY_TARGET} per category`} />
        </Grid>
      </Grid>

      {/* Category cards grid */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Category Breakdown</Typography>
      {rows.length === 0 ? (
        <Alert severity="info">No category data available. Add partners with services to see stats.</Alert>
      ) : (
        <Grid container spacing={2}>
          {rows.map(row => {
            const pct = Math.min(100, Math.round((row.total / CATEGORY_TARGET) * 100));
            const barColor = pct >= 100 ? 'success' : pct >= 60 ? 'warning' : 'error';
            return (
              <Grid item xs={12} sm={6} md={4} key={row.categoryId}>
                <Card sx={{ height: '100%', borderLeft: `4px solid`,
                  borderLeftColor: pct >= 100 ? 'success.main' : pct >= 60 ? 'warning.main' : 'error.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>{row.name}</Typography>
                        <Chip label={row.type} size="small" variant="outlined"
                          color={row.type === 'daily' ? 'info' : 'secondary'} sx={{ mt: 0.5 }} />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h4" fontWeight="bold" color={`${barColor}.main`}>
                          {row.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">partners</Typography>
                      </Box>
                    </Box>

                    <LinearProgress variant="determinate" value={pct} color={barColor}
                      sx={{ height: 8, borderRadius: 4, mb: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={`✓ ${row.verified} verified`} size="small" color="success" variant="outlined" />
                        {row.unverified > 0 &&
                          <Chip label={`${row.unverified} pending`} size="small" color="warning" variant="outlined" />}
                      </Box>
                      {row.needed > 0 ? (
                        <Chip label={`Need ${row.needed} more`} size="small" color="error" />
                      ) : (
                        <Chip label="Target met ✓" size="small" color="success" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Detailed table */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Detailed Partner List by Category</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Total Partners</TableCell>
              <TableCell align="center">Verified</TableCell>
              <TableCell align="center">Unverified</TableCell>
              <TableCell align="center">Target</TableCell>
              <TableCell align="center">Still Needed</TableCell>
              <TableCell>Partners</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.categoryId} hover>
                <TableCell><Typography variant="body2" fontWeight="bold">{row.name}</Typography></TableCell>
                <TableCell>
                  <Chip label={row.type} size="small" variant="outlined"
                    color={row.type === 'daily' ? 'info' : 'secondary'} />
                </TableCell>
                <TableCell align="center"><Typography fontWeight="bold">{row.total}</Typography></TableCell>
                <TableCell align="center">
                  <Typography color="success.main" fontWeight="bold">{row.verified}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography color="warning.main" fontWeight="bold">{row.unverified}</Typography>
                </TableCell>
                <TableCell align="center">{CATEGORY_TARGET}</TableCell>
                <TableCell align="center">
                  {row.needed > 0
                    ? <Chip label={row.needed} size="small" color="error" />
                    : <Chip label="✓" size="small" color="success" />}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {row.partners.slice(0, 5).map(p => (
                      <Tooltip key={p.userId} title={`${p.name} • ${p.status}`}>
                        <AvatarInitials name={p.name} color={p.status === 'verified' ? 'success' : 'warning'} />
                      </Tooltip>
                    ))}
                    {row.partners.length > 5 &&
                      <Avatar sx={{ width: 30, height: 30, fontSize: 11, bgcolor: 'grey.400' }}>
                        +{row.partners.length - 5}
                      </Avatar>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── TAB 3 : BOOKINGS ─────────────────────────────────────────────────────────

function BookingsTab() {
  const [bookings, setBookings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(15);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      // Use serviceRequestService — same method that works in ServiceRequests page
      const data = await serviceRequestService.getServiceRequests();
      setBookings(data || []);
    } catch (e) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Real statuses: pending, upcoming, ongoing, completed, cancelled
  const stats = {
    total:     bookings.length,
    pending:   bookings.filter(b => b.status === 'pending').length,
    upcoming:  bookings.filter(b => b.status === 'upcoming').length,
    ongoing:   bookings.filter(b => b.status === 'ongoing').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    revenue:   bookings
                 .filter(b => b.status === 'completed')
                 .reduce((s, b) => s + (Number(b.totalAmount) || 0), 0),
  };

  const filtered = bookings.filter(b => {
    const matchStatus = statusFilter === 'all' || b.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (b.customerName  || '').toLowerCase().includes(q) ||
      (b.mobileNumber  || '').toLowerCase().includes(q) ||
      (b.bookingId     || '').toLowerCase().includes(q) ||
      (b.service?.name || '').toLowerCase().includes(q) ||
      (b.partner?.name || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const handleStatusChange = (_, v) => { setStatusFilter(v); setPage(0); };
  const handleSearchChange = (e)    => { setSearch(e.target.value); setPage(0); };

  const statusTabs = [
    { value: 'all',       label: `All (${stats.total})` },
    { value: 'pending',   label: `Pending (${stats.pending})` },
    { value: 'upcoming',  label: `Upcoming (${stats.upcoming})` },
    { value: 'ongoing',   label: `Ongoing (${stats.ongoing})` },
    { value: 'completed', label: `Completed (${stats.completed})` },
    { value: 'cancelled', label: `Cancelled (${stats.cancelled})` },
  ];

  const statusChipColor = {
    pending:   'warning',
    upcoming:  'info',
    ongoing:   'primary',
    completed: 'success',
    cancelled: 'error',
  };

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={2}>
          <StatCard title="Total"     value={stats.total}     icon={<BookOnline />}      color="primary" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <StatCard title="Pending"   value={stats.pending}   icon={<HourglassEmpty />}  color="warning" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <StatCard title="Upcoming"  value={stats.upcoming}  icon={<Assignment />}      color="info" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <StatCard title="Ongoing"   value={stats.ongoing}   icon={<Assignment />}      color="primary" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <StatCard title="Completed" value={stats.completed} icon={<CheckCircle />}     color="success" />
        </Grid>
        <Grid item xs={6} sm={2}>
          <StatCard title="Revenue"   value={`₹${stats.revenue.toLocaleString('en-IN')}`}
            icon={<TrendingUp />} color="success" subtitle="From completed" />
        </Grid>
      </Grid>

      {/* Status filter tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={statusFilter} onChange={handleStatusChange} variant="scrollable">
          {statusTabs.map(t => <Tab key={t.value} label={t.label} value={t.value} />)}
        </Tabs>
      </Paper>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by customer, mobile, booking ID, service or partner…"
          value={search}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
        Showing <strong>{filtered.length}</strong> of <strong>{stats.total}</strong> booking{stats.total !== 1 ? 's' : ''}
      </Typography>

      {/* Bookings table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>No bookings found</Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.map(b => (
                <TableRow key={b.serviceRequestId} hover
                  sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                      {b.bookingId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AvatarInitials name={b.customerName} color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{b.customerName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{b.mobileNumber || '—'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{b.service?.name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {b.service?.categoryName || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {b.partner ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AvatarInitials name={b.partner.name} color="success" />
                        <Box>
                          <Typography variant="body2">{b.partner.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{b.partner.mobileNumber || ''}</Typography>
                        </Box>
                      </Box>
                    ) : <Chip label="Unassigned" size="small" color="warning" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {b.serviceDate ? moment(b.serviceDate).format('DD MMM YYYY') : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{b.address || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {b.partnerPinCode || b.pincode || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      ₹{(Number(b.totalAmount) || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={b.status}
                      color={statusChipColor[b.status] || 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {b.userReview?.customer ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="warning.main" fontWeight="bold">
                          {'★'.repeat(Math.min(Number(b.userReview.customer), 5))}
                        </Typography>
                        <Typography variant="caption">({b.userReview.customer})</Typography>
                      </Box>
                    ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}

// ─── MAIN MANAGER PAGE ────────────────────────────────────────────────────────

const TABS = [
  { label: 'Requests',           icon: <Assignment />,  component: <RequestsTab /> },
  { label: 'Partner Categories', icon: <People />,      component: <PartnerCategoriesTab /> },
  // { label: 'Bookings',           icon: <BookOnline />,  component: <BookingsTab /> },
];

const Manager = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Manager Overview</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Full visibility into requests, partner strength per category, and booking details.
      </Typography>

      <Paper sx={{ mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((t, i) => (
            <Tab
              key={i}
              label={t.label}
              icon={t.icon}
              iconPosition="start"
              sx={{ minHeight: 56 }}
            />
          ))}
        </Tabs>
      </Paper>

      {TABS.map((t, i) => (
        <TabPanel key={i} value={tab} index={i}>
          {t.component}
        </TabPanel>
      ))}
    </Box>
  );
};

export default Manager;
